import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { Button, Dropdown, Form, Input, Select } from "semantic-ui-react";
import { Config } from "../../../config/api";
import { checkIfAdminAgency, getReadableClientName } from "../../../libs/common_utils";
import { useForm } from "../../../libs/component_utils";
import { default1PSeller } from "../../../libs/consts";
import HttpConnect from "../../../libs/http_connect";
import AgenciesService from "../../../services/agencies";
import PermissionsService from "../../../services/permissions";
import PixelsService from "../../../services/pixels";
import T1Service from "../../../services/t1";
import BrandSkuPicker from "../../common/BrandSkuPicker";

const shouldDoSearch = (query, prevQuery) => {
  return prevQuery !== query && query.length >= 1;
};
const clientName = getReadableClientName(Config.public_client);

function validate(values, intl, selectedSkus) {
  const errors = {};

  if (!values.advertiser_id) {
    errors.advertiser_id = intl.formatMessage({
      id: "ERROR_EMPTY_ADVERTISER_ID",
      defaultMessage: "Please select an advertiser.",
    });
  }

  if (!values.seller_id) {
    errors.seller_id = intl.formatMessage({
      id: "VALIDATOR_SELECT_SELLER",
      defaultMessage: "Please select a seller",
    });
  }

  if (!values.event_type) {
    errors.event_type = intl.formatMessage({
      id: "VALIDATOR_SELECT_EVENT_TYPE",
      defaultMessage: "Please select an event type",
    });
  }

  if (values.brand_names.length < 1) {
    errors.brand_names = intl.formatMessage({
      id: "VALIDATOR_SELECT_BRAND",
      defaultMessage: "Please select at least one Brand",
    });
  }

  if (values.attribution_level === "sku" && selectedSkus.length < 1) {
    errors.sku_ids = intl.formatMessage({
      id: "VALIDATOR_SKU_NUMBERS",
      defaultMessage: "Please select at least one SKU.",
    });
  }

  return errors;
}

const RetargetingSegmentsForm = ({
  initialData,
  isSubmitting,
  onSubmit,
  onCancel,
  formType = "create",
  submitText = "Create Brand and SKU Segment",
  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: Should consider moving advertiser and seller dropdown logic to their own components
}) => {
  const intl = useIntl();
  const services = useRef(
    new Map([
      ["pixels", new PixelsService()],
      ["t1", new T1Service()],
      ["agencies", new AgenciesService()],
      ["permissions", new PermissionsService()],
    ]),
  );
  const sellerQuery = useRef("");
  const sellerTimer = useRef(0);
  const agency = services.current.get("agencies").getSelectedAgency() || 0;
  const [agencyPermissions, setAgencyPermissions] = useState([]);
  const [filteredPermissions, setFilteredPermissions] = useState([]);
  const [enabledAdvertiserIds, setEnabledAdvertiserIds] = useState([]);
  const [hasFetchedPermissions, setHasFetchedPermissions] = useState(false);
  const [fetchedSellers, setFetchedSellers] = useState([]);
  const permissionsActiveRef = useRef(false);

  const isEditmode = formType.toString() === "edit";
  const [selectedSkus, setSelectedSkus] = useState(() => {
    if (isEditmode && initialData?.sku_ids) {
      const ids =
        typeof initialData.sku_ids === "string"
          ? initialData.sku_ids
              .split(",")
              .map((id) => id.trim())
              .filter((id) => id.length > 0)
          : Array.isArray(initialData.sku_ids)
            ? initialData.sku_ids
            : [];
      const itemNames = initialData?.item_names || {};
      // biome-ignore lint/style/useNamingConvention: matches API data shape used throughout
      return ids.map((sku_id) => ({ sku_id, item_name: itemNames[sku_id] || null }));
    }
    return [];
  });
  const [pageData, setPageData] = useState(() => {
    const initialSellers = [
      {
        key: "hint",
        text: intl.formatMessage({
          id: "PLACEHOLDER_START_TYPING_3P",
          defaultMessage: "Start typing to search for 3P sellers...",
        }),
        value: "hint",
        disabled: true,
      },
    ];

    // If in edit mode and we have seller data, add the current seller
    if (isEditmode && initialData.seller_id) {
      // Skip if it's the 1P seller
      if (initialData.seller_id !== "0") {
        initialSellers.push({
          key: initialData.seller_id,
          text: initialData.seller_id,
          value: initialData.seller_id,
        });
      }
    }

    return {
      advertisers: [],
      advertisersLoading: true,
      sellers: initialSellers,
      sellersLoading: false,
    };
  });

  const get1pAdvertiserIds = useCallback((permissions) => {
    return permissions.flatMap((permission) => permission.advertisers.map((advertiser) => advertiser.id));
  }, []);

  /**
   * initially loading advertisers list
   */

  useEffect(() => {
    let active = true;
    const loadAdvertisers = async () => {
      try {
        const res = await services.current.get("t1").advertisers(agency);
        if (!active) return;
        setPageData((prev) => ({
          ...prev,
          advertisersLoading: false,
          advertisers: res.data.map(({ id, title }) => ({ key: id, text: title, value: id })),
        }));
      } catch (e) {
        console.error(e);
      }
    };
    loadAdvertisers();
    return () => {
      active = false;
    };
  }, [agency]);

  const fetchAgencyPermissions = useCallback(async () => {
    try {
      const response = await services.current
        .get("permissions")
        // biome-ignore lint/style/useNamingConvention: API parameter
        .getAllPermissionsForROIPixelPage({ agency_id: agency });
      if (!permissionsActiveRef.current) return;
      const currentAgencyPermissions = response.data.filter((permission) => permission.agency_id === agency);
      const filteredPermissions = currentAgencyPermissions.filter(
        (permission) => permission.seller_ids[0] === default1PSeller.value,
      );
      setAgencyPermissions(currentAgencyPermissions);
      setFilteredPermissions(filteredPermissions);
      setEnabledAdvertiserIds(get1pAdvertiserIds(filteredPermissions));
    } catch (error) {
      console.error("Error fetching permissions:", error);
    } finally {
      if (permissionsActiveRef.current) setHasFetchedPermissions(true);
    }
  }, [get1pAdvertiserIds, agency]);

  useEffect(() => {
    permissionsActiveRef.current = true;
    fetchAgencyPermissions();
    return () => {
      permissionsActiveRef.current = false;
    };
  }, [fetchAgencyPermissions]);

  const addRetargetingSegment = async () => {
    try {
      // biome-ignore lint/style/useNamingConvention: API parameters
      await onSubmit({ ...values, agency_id: agency, sku_ids: selectedSkus.map((s) => s.sku_id).join(",") });
    } catch (error) {
      console.error("Error adding Brand and SKU segment:", error);
    }
  };

  const {
    values,
    errors,
    onChange,
    onSubmit: handleSubmit,
    updateValues,
  } = useForm(addRetargetingSegment, initialData, () => validate(values, intl, selectedSkus));

  const isAdminAgency = hasFetchedPermissions && checkIfAdminAgency(agencyPermissions);
  // Advertiser is 1P enabled if:
  // 1. The advertiser is in the list of enabled advertisers from permissions
  // 2. Or if there is only one permission and it has no advertisers (which means the 1P rule applies to all advertisers)
  const is1PEnabledAdvertiser =
    (hasFetchedPermissions && enabledAdvertiserIds.includes(values.advertiser_id?.toString())) ||
    (filteredPermissions.length === 1 && filteredPermissions[0].advertisers.length === 0) ||
    isAdminAgency;

  useEffect(() => {
    if (is1PEnabledAdvertiser) {
      setPageData((prev) => ({
        ...prev,
        sellers: [...prev.sellers.filter((seller) => seller.value !== default1PSeller.value), default1PSeller],
      }));
    } else {
      setPageData((prev) => ({
        ...prev,
        sellers: prev.sellers.filter((seller) => seller.value !== default1PSeller.value),
      }));
    }
  }, [is1PEnabledAdvertiser]);

  const handleSellersSearch = (_e, data) => {
    const prevSellerQuery = sellerQuery.current || "";
    sellerQuery.current = data.searchQuery;
    if (sellerTimer.current) clearTimeout(sellerTimer.current);
    if (!shouldDoSearch(sellerQuery.current, prevSellerQuery)) {
      setPageData((prev) => ({
        ...prev,
        sellers: is1PEnabledAdvertiser ? [default1PSeller] : [],
        sellersLoading: false,
      }));
      return;
    }
    setPageData((prev) => ({ ...prev, sellers: [], sellersLoading: true }));
    HttpConnect.cancelRequest();

    sellerTimer.current = setTimeout(async () => {
      try {
        const sellers = await services.current.get("pixels").getSellers(sellerQuery.current, {
          // biome-ignore lint/style/useNamingConvention: API parameter
          apply_permissions: true,
          // biome-ignore lint/style/useNamingConvention: API parameter
          advertiser_id: values.advertiser_id,
          // biome-ignore lint/style/useNamingConvention: API parameter
          agency_id: agency,
        });
        setFetchedSellers(sellers.data);
        if (sellers.data) {
          const formattedSellers = [
            ...(is1PEnabledAdvertiser ? [default1PSeller] : []),
            ...sellers.data.map((s) => {
              return {
                key: s.seller_id,
                text: s.seller_id,
                value: s.seller_id,
              };
            }),
          ];
          setPageData((prev) => ({ ...prev, sellers: formattedSellers, sellersLoading: false }));
        } else {
          setPageData((prev) => ({
            ...prev,
            sellers: is1PEnabledAdvertiser ? [default1PSeller] : [],
            sellersLoading: false,
          }));
        }
      } catch (e) {
        console.error("Error fetching sellers:", e);
        setPageData((prev) => ({ ...prev, sellers: [], sellersLoading: false }));
      }
    }, Config.search_debounce_delay);
  };

  const handleAdvertiserChange = (_e, data) => {
    const { value } = data;
    if (value !== values.advertiser_id) {
      updateValues({
        ...values,
        // biome-ignore lint/style/useNamingConvention: form field names match API shape
        advertiser_id: value,
        // biome-ignore lint/style/useNamingConvention: form field names match API shape
        brand_names: [],
        // biome-ignore lint/style/useNamingConvention: form field names match API shape
        sku_ids: "",
        // biome-ignore lint/style/useNamingConvention: form field names match API shape
        seller_id: null,
        // biome-ignore lint/style/useNamingConvention: form field names match API shape
        seller_name: null,
      });
      setSelectedSkus([]);
    }
  };

  const eventTypes = useMemo(
    () => [
      {
        text: intl.formatMessage({
          id: "LABEL_PURCHASE_EVENTS",
          defaultMessage: "Purchase Events",
        }),
        value: "purchase",
      },
      {
        text: intl.formatMessage({
          id: "LABEL_INTENT_EVENTS",
          defaultMessage: "Intent Events",
        }),
        value: "intent",
      },
      {
        text: intl.formatMessage({
          id: "LABEL_AFFINITY_EVENTS",
          defaultMessage: "Affinity Events",
        }),
        value: "affinity",
      },
    ],
    [intl],
  );

  return (
    <Form
      autoComplete="off"
      error={!!Object.keys(errors).length}
      onSubmit={handleSubmit}
      size="small"
      noValidate
    >
      <Form.Field
        inline
        error={Object.hasOwn(errors, "name")}
        required
      >
        <label>
          {intl.formatMessage({
            id: "LABEL_SEGMENT_NAME",
            defaultMessage: "Segment Name",
          })}
        </label>
        <Input
          aria-label="Segment Name"
          data-testid="retargeting-segment-name-input"
          name="name"
          required
          minLength={1}
          maxLength={256}
          defaultValue={values.name}
          onBlur={onChange}
        />
        <div className="custom-error">{errors?.name}</div>
      </Form.Field>

      <Form.Field
        inline
        required
        error={Object.hasOwn(errors, "event_type")}
      >
        <label>
          {intl.formatMessage({
            id: "LABEL_EVENT_TYPE",
            defaultMessage: "Event Type",
          })}
        </label>
        <div
          className="custom-error-container"
          style={{ width: "25%" }}
        >
          <Select
            data-testid="retargeting-segment-event-type"
            disabled={isEditmode}
            name="event_type"
            options={eventTypes}
            placeholder={intl.formatMessage({
              id: "HINT_EVENT_TYPE",
              defaultMessage: "Select event type",
            })}
            selection
            value={values.event_type}
            onChange={(_e, { value }) => {
              onChange(null, { name: "event_type", value });
            }}
            style={{ width: "100%" }}
          />
        </div>
        <div className="custom-error">{errors?.event_type}</div>
      </Form.Field>

      <Form.Field
        inline
        error={Object.hasOwn(errors, "advertiser_id")}
        required
      >
        <label>
          {intl.formatMessage({
            id: "LABEL_ADVERTISER",
            defaultMessage: "Advertiser",
          })}
        </label>
        <div
          className="custom-error-container"
          style={{ width: "25%" }}
        >
          <Dropdown
            data-testid="retargeting-segment-advertiser"
            required
            search
            selection
            disabled={isEditmode}
            options={pageData.advertisers}
            loading={pageData.advertisersLoading}
            placeholder={intl.formatMessage({
              id: "HINT_ADVERTISER",
              defaultMessage: "Select advertiser",
            })}
            noResultsMessage={intl.formatMessage({
              id: "EMPTY_SEARCH_RESULTS",
              defaultMessage: "No results found",
            })}
            name="advertiser_id"
            value={values.advertiser_id}
            onChange={handleAdvertiserChange}
            style={{ width: "100%" }}
          />
          <div className="custom-error">{errors?.advertiser_id}</div>
        </div>
      </Form.Field>
      <Form.Field
        disabled={!values.advertiser_id}
        inline
        error={Object.hasOwn(errors, "seller_id")}
        required
      >
        <label>
          {intl.formatMessage({
            id: "LABEL_SELLER",
            defaultMessage: "Seller",
          })}
        </label>
        <div
          className="custom-error-container"
          style={{ width: "25%" }}
        >
          <Dropdown
            data-testid="retargeting-segment-seller"
            disabled={!values.advertiser_id || isEditmode}
            required
            search
            selection
            options={pageData.sellers}
            loading={pageData.sellersLoading}
            placeholder={intl.formatMessage({
              id: "PLACEHOLDER_SELECT_SELLER",
              defaultMessage: "Select Seller",
            })}
            noResultsMessage={
              pageData.sellersLoading
                ? ""
                : sellerQuery.current.length
                  ? intl.formatMessage({
                      id: "PLACEHOLDER_NOT_FOUND",
                      defaultMessage: "Not found",
                    })
                  : intl.formatMessage({
                      id: "PLACEHOLDER_START_TYPING_3P",
                      defaultMessage: "Start typing to search for 3P sellers...",
                    })
            }
            name="seller_id"
            value={values.seller_id}
            onSearchChange={handleSellersSearch}
            onChange={(_e, { value }) => {
              const seller3P = fetchedSellers.find((seller) => seller.seller_id === value);
              const sellerName = seller3P ? seller3P.seller_name : clientName;

              updateValues({
                ...values,
                // biome-ignore lint/style/useNamingConvention: form field names match API shape
                seller_id: value,
                // biome-ignore lint/style/useNamingConvention: form field names match API shape
                seller_name: sellerName,
                // biome-ignore lint/style/useNamingConvention: form field names match API shape
                brand_names: [],
                // biome-ignore lint/style/useNamingConvention: form field names match API shape
                sku_ids: "",
              });
              setSelectedSkus([]);
            }}
            style={{ width: "100%" }}
          />
          {errors?.seller_id && <div className="custom-error">{errors.seller_id}</div>}
        </div>
      </Form.Field>
      <BrandSkuPicker
        key={`${values.advertiser_id}-${values.seller_id}`}
        attribution_level={values.attribution_level}
        onAttributionLevelChange={onChange}
        attributionLevelLabel={intl.formatMessage({ id: "LABEL_TARGETING_LEVEL", defaultMessage: "Targeting Level" })}
        brand_names={values.brand_names}
        onBrandNamesChange={(newBrands) => updateValues({ ...values, brand_names: newBrands })}
        initialBrandOptions={initialData?.brand_names?.map((brand) => ({ text: brand, value: brand })) || []}
        advertiser_id={values.advertiser_id}
        seller_id={values.seller_id}
        agency={agency}
        disabled={values.seller_id === null || isEditmode}
        brandDisabled={isEditmode}
        errors={{ brand_names: errors.brand_names, sku_ids: errors.sku_ids }}
        selectedSkus={selectedSkus}
        setSelectedSkus={setSelectedSkus}
      />

      <Form.Field align="right">
        <Button
          size="tiny"
          type="button"
          onClick={onCancel}
        >
          {intl.formatMessage({
            id: "BTN_CANCEL",
            defaultMessage: "Cancel",
          })}
        </Button>
        <Button
          data-testid="retargeting-segment-submit"
          size="tiny"
          color="green"
          type="submit"
          disabled={isSubmitting}
          loading={isSubmitting}
        >
          {submitText}
        </Button>
      </Form.Field>
    </Form>
  );
};

export default RetargetingSegmentsForm;

RetargetingSegmentsForm.propTypes = {
  initialData: PropTypes.object,
  isSubmitting: PropTypes.bool,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func,
  formType: PropTypes.oneOf(["create", "edit"]),
  submitText: PropTypes.string,
};
